import React from 'react'
import {
  FormControl,
  IconButton,
  MenuItem,
  MenuProps,
  Select,
  StyleProps,
  SxProps,
  Tooltip,
} from '@mui/material'
import { ChevronDown, X } from 'lucide-react'

import { palette } from '@core/styles/colors'

const styles = {
  formControl: {
    '& .MuiInputBase-root': {
      minWidth: 240,
      height: 40,
    },
    '& .MuiInputBase-sizeSmall': {
      minWidth: 60,
      height: 40,
    },
  },
  icon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  select: {
    width: '100%',
    height: '100%',
    py: 0.5,
    pl: 0,
    pr: 0.5,
    borderRadius: 2,
    fontWeight: 500,
    border: '2px solid #e1e3e8',
    backgroundColor: palette.neutral.N010,
    outline: 'none',
    fontSize: '0.875rem',
    transition: 'box-shadow 300ms ease, border 300ms ease',
    '&:hover': {
      boxShadow: `inset 0 0 0 2px ${palette.neutral.N090}`,
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      boxShadow: `none`,
      '& svg': {
        opacity: 0.5,
      },
    },
    '&.Mui-focused': {
      border: `2px solid ${palette.blue.B200}`,
      boxShadow: `inset 0 0 0 2px ${palette.blue.B100}`,
    },
    '&.Mui-error': {
      backgroundColor: palette.red.R050,
      border: `2px solid ${palette.red.R400}`,
      color: palette.red.R400,
      '&:hover': {
        boxShadow: `inset 0 0 0 2px ${palette.red.R400}`,
      },
      '& .lucide': {
        stroke: palette.red.R400,
      },
    },
    '& fieldset': {
      border: 'none',
      '&:active': {
        border: `2px solid ${palette.neutral.N090}`,
      },
    },
  },
} satisfies StyleProps

const menuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },
  PaperProps: {
    sx: {
      boxShadow: '0 8px 16px -4px rgba(70, 78, 88, 0.2), 0 0 1px 0 rgba(70, 78, 88, 0.2)',
      background: palette.neutral.N000,
      minWidth: 240,
      maxWidth: 600,
      borderRadius: 2,
      '& .MuiMenuItem-root': {
        paddingY: 1,
        paddingX: 2,
        textWrap: 'auto',
        fontSize: '0.875rem',
        fontWeight: 500,
        '&:hover': {
          backgroundColor: palette.neutral.N010,
        },
      },
    },
  },
}

export interface DropdownOption {
  value: string | number
  name: string
  disabled?: boolean
  tooltip?: string
}

const DropdownChevron = () => (
  <ChevronDown size={20} color={palette.neutral.N400} style={styles.icon as React.CSSProperties} />
)

interface DropdownProps {
  value: string | number | DropdownOption | null
  onChange: (value: any) => void
  options: DropdownOption[]
  label?: string
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  sx?: SxProps
  testid?: string
  required?: boolean
  error?: boolean
  showLabelAsOption?: boolean
  clearable?: boolean
  onClear?: () => void
  menuProps?: Partial<MenuProps>
}

const Dropdown = ({
  value,
  onChange,
  options = [],
  label,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  sx = {},
  testid,
  required = false,
  error = false,
  showLabelAsOption = false,
  clearable = false,
  onClear,
  menuProps: customMenuProps,
}: DropdownProps) => {
  const handleChange = (option) => {
    onChange(option)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onClear) {
      onClear()
    } else {
      onChange({ name: undefined, value: undefined })
    }
  }

  const hasValue = value !== null && value !== undefined && value !== '' && value !== label

  return (
    <FormControl
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
      sx={{ ...styles.formControl, ...sx }}
      required={required}
      error={error || (required && !value)}
    >
      <Select
        id={testid}
        required={required}
        data-testid={testid}
        value={value && typeof value === 'object' ? value.value : value}
        IconComponent={DropdownChevron}
        endAdornment={
          clearable &&
          hasValue &&
          !disabled && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                p: 0.25,
                mr: 3.5,
                '&:hover': { backgroundColor: palette.neutral.N030 },
              }}
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X size={16} color={palette.neutral.N400} />
            </IconButton>
          )
        }
        renderValue={(val: string | number | DropdownOption): React.ReactNode => {
          if (typeof val === 'object') {
            return val?.name
          }
          const match = options.find((o) => o.value === val)
          return match?.name || val || label || ''
        }}
        displayEmpty={!!label}
        sx={styles.select}
        MenuProps={{
          ...menuProps,
          ...customMenuProps,
          PaperProps: {
            ...menuProps.PaperProps,
            ...customMenuProps?.PaperProps,
            sx: [
              menuProps.PaperProps?.sx,
              customMenuProps?.PaperProps?.sx,
              customMenuProps?.sx,
            ] as any,
          },
        }}
      >
        {label &&
          (showLabelAsOption ? (
            <MenuItem value="" disabled={!value} onClick={() => handleChange({ name: undefined })}>
              {label}
            </MenuItem>
          ) : (
            <MenuItem value="" disabled>
              {label}
            </MenuItem>
          ))}

        {options.map((option) => {
          const isSelected =
            typeof value === 'object' ? option.value === value?.value : option.value === value

          const menuItem = (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                backgroundColor: isSelected ? 'rgba(23, 43, 77, 0.08);' : 'transparent',
              }}
              onClick={() => handleChange(option)}
              disabled={option.disabled || isSelected}
              data-testid={`${testid}-option-${option.value}`}
            >
              {option.name}
            </MenuItem>
          )

          if (option.tooltip && option.disabled) {
            return (
              <Tooltip key={option.value} title={option.tooltip} placement="right">
                <span>{menuItem}</span>
              </Tooltip>
            )
          }

          return menuItem
        })}
      </Select>
    </FormControl>
  )
}

export default Dropdown
